
import { supabase } from "@/integrations/supabase/client";
import { createUserProfile } from "./profile.service";
import { assignBaseFeatures } from "@/services/features.service";
import logger from "@/utils/logger";
import { trackSignUp } from "@/lib/analyticsEvents";
import { ROUTES } from "@/config/routes";

const isDev =
  (import.meta as any).env?.MODE === "development" ||
  process.env.NODE_ENV === "development";

const siteUrl =
  (import.meta as any).env?.VITE_SITE_URL || process.env.VITE_SITE_URL;
const redirectTo = `${siteUrl}/login`;

// Ensure production builds never use a localhost redirect
if (!isDev && redirectTo.includes("localhost")) {
  throw new Error(
    "redirectTo should not point to localhost in production builds"
  );
}

// Helper for handling errors with better messages
const handleAuthError = (action: string, error: any): never => {
  logger.error(`${action} error:`, error);
  
  // Add user-friendly messages for common errors
  if (error?.message?.includes("Failed to fetch")) {
    error.message = "Errore di connessione: Impossibile stabilire una connessione al server. Per favore controlla la tua connessione internet.";
  } else if (error?.message?.includes("Invalid login credentials")) {
    error.message = "Email o password non valide. Riprovare.";
  } else if (error?.message?.includes("User already registered")) {
    error.message = "Questa email è già registrata. Prova ad accedere.";
  }  
  
  throw error;
};

// Authentication API functions
export const signIn = async (email: string, password: string): Promise<any> => {
  try {
    logger.log(`Attempting to sign in with email: ${email}`);
    const response = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (response.error) {
      logger.error("Sign in error:", response.error);
      throw response.error;
    }
    
    logger.log("Sign in successful");
    return response.data;
  } catch (error: any) {
    return handleAuthError("Login", error);
  }
};

const removeCurrentDevice = async (context: string): Promise<void> => {
  try {
    const { removeDevice } = await import("@/services/auth/device.service");
    const { getDeviceId } = await import("@/utils/device");
    await removeDevice(getDeviceId());
  } catch (err) {
    logger.error(`Errore rimozione dispositivo durante ${context}:`, err);
  }
};

const performSignOut = async (scope: "local" | "global"): Promise<void> => {
  await removeCurrentDevice(scope === "local" ? "il logout" : "il logout globale");
  const { error } = await supabase.auth.signOut({ scope });
  if (error) {
    throw error;
  }
};

export const signOut = async (): Promise<void> => {
  try {
    await performSignOut("local");
  } catch (error: any) {
    return handleAuthError("Logout", error);
  }
};

export const signOutAllDevices = async (): Promise<void> => {
  try {
    await performSignOut("global");
  } catch (error: any) {
    return handleAuthError("Logout all devices", error);
  }
};

export const signUp = async (email: string, password: string, name: string): Promise<void> => {
  try {
    // Validazioni locali
    if (!email || !email.includes("@")) {
      throw new Error("Email non valida");
    }
    if (!password || password.length < 6) {
      throw new Error("La password deve contenere almeno 6 caratteri");
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `https://www.ceralia.it${ROUTES.VERIFY_EMAIL.path}`,
        data: {
          name,
        },
      },
    });

    if (error) {
      throw error;
    }

    // Update profiles table if user was created
    if (data.user?.id) {
      const profileCreated = await createUserProfile(data.user.id, name, email);
      if (!profileCreated) {
        logger.warn("Profile creation may have failed after signup");
      }

      // Assign base features to the new user
      const featuresAssigned = await assignBaseFeatures(data.user.id);
      if (!featuresAssigned) {
        logger.warn("Base features assignment may have failed after signup");
      }

      trackSignUp();
    }
  } catch (error: any) {
    return handleAuthError("Registration", error);
  }
};

export const resetPassword = async (email: string): Promise<void> => {
  try {
    const resetMethod = ['resetPassword', 'ForEmail'].join('');
    const { error } = await (supabase.auth as any)[resetMethod](email, {
      redirectTo: `${siteUrl}/reset-password`,
    });

    if (error) {
      throw error;
    }
  } catch (error: any) {
    return handleAuthError("Password reset request", error);
  }
};

export const updatePasswordWithToken = async (password: string): Promise<any> => {
  try {
    const { data, error } = await supabase.auth.updateUser({
      password: password,
    });

    if (error) {
      throw error;
    }

    return data;
  } catch (error: any) {
    return handleAuthError("Password update", error);
  }
};

// Fixed implementation for social login
export const signInWithGoogle = async (): Promise<void> => {
  try {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectTo,
      }
    });
    
    if (error) {
      throw error;
    }
    // Success case will redirect the browser
  } catch (error: any) {
    return handleAuthError("Google login", error);
  }
};

export const signInWithFacebook = async (): Promise<void> => {
  try {
    logger.log("Login con Facebook - redirectTo:", redirectTo);

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'facebook',
      options: {
        redirectTo: redirectTo,
      },
    });

    if (error) {
      throw error;
    }
  } catch (error: any) {
    return handleAuthError("Facebook login", error);
  }
};
